import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.css';
import { GroupProduct, Product, ProductType } from '@meitu/subscribe-core';
import { getDisplayPrice, getDurationPeriod, getIncomeSource } from '@/utils/vip';
import { getSkuType, getUrlParam } from '@/utils';
import { useAtom } from 'jotai';
import { activeIndexAtom, curProductAtom, curTabAtom, loadingAtom, productsAtom } from '@/store';
import { track } from '@/utils/common';

const ProductListUnlimited = () => {
  const [loading] = useAtom(loadingAtom);
  const [products] = useAtom(productsAtom);
  const [activeIndex, setActiveIndex] = useAtom(activeIndexAtom);
  const [curProduct] = useAtom(curProductAtom);

  const handleSelectChange = (index: number) => {
    if (loading) return;

    setActiveIndex(index);

    const current = products[index] as Product;

    let params: any = {};
    if (getIncomeSource() === 'MTXX-WEB') {
      params = {
        class: 'name_id',
        name_id: current?.productId,
        country_code: current?.countryCode || '',
        sku_type: getSkuType(),
      };
    } else {
      params = {
        category: current?.productName,
        product_id: current?.productId,
        country_code: current?.countryCode || '',
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });
  };

  const renderCard = (data: Product, index: number) => {
    return (
      <div
        className={classNames(styles.card, {
          [styles.cardActived]: activeIndex === index,
        })}
        onClick={() => handleSelectChange(index)}
        key={`card_${index}`}
      >
        {activeIndex === index ? (
          <div className={styles.checkedIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M14.8687 2.95441C15.262 3.34216 15.2664 3.97531 14.8787 4.36859L6.82913 12.8686C6.6367 13.0638 6.37259 13.1714 6.09854 13.1663C5.8245 13.1613 5.56455 13.0439 5.37947 12.8418L1.09568 7.81399C0.722744 7.40664 0.750641 6.77409 1.15799 6.40115C1.56534 6.02821 2.19789 6.05611 2.57083 6.46346L6.1439 10.7149L13.4545 2.96442C13.8422 2.57114 14.4754 2.56666 14.8687 2.95441Z"
                fill="white"
              />
            </svg>
          </div>
        ) : null}
        <div className={styles.top}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className={styles.icon}
          >
            <path
              d="M10.7031 10.1484L10.4141 11.5H6.46485L7.66406 5.86328H9.32031L8.41016 10.1484H10.7031Z"
              fill="white"
            />
            <path
              d="M14.9305 11.5977C14.5372 11.5977 14.1779 11.5378 13.8523 11.418C13.5268 11.2956 13.256 11.1276 13.0398 10.9141C12.8237 10.6979 12.6557 10.444 12.5359 10.1523C12.4188 9.85807 12.3602 9.54037 12.3602 9.19922C12.3602 8.69401 12.4409 8.22656 12.6023 7.79688C12.7664 7.36458 12.9891 7.0013 13.2703 6.70703C13.5516 6.41016 13.8823 6.17969 14.2625 6.01563C14.6453 5.84896 15.0516 5.76563 15.4813 5.76563C15.8849 5.76563 16.2508 5.82682 16.5789 5.94922C16.907 6.07162 17.1792 6.24089 17.3953 6.45703C17.6115 6.67057 17.7768 6.92318 17.8914 7.21485C18.0086 7.50651 18.0672 7.82292 18.0672 8.16406C18.0672 8.67188 17.9839 9.14193 17.8172 9.57422C17.6531 10.0065 17.4292 10.3698 17.1453 10.6641C16.8641 10.9557 16.532 11.1849 16.1492 11.3516C15.7664 11.5156 15.3602 11.5977 14.9305 11.5977ZM15.4422 7.08594C15.1922 7.08594 14.9591 7.17578 14.743 7.35547C14.5294 7.53516 14.3589 7.77995 14.2313 8.08985C14.1036 8.39974 14.0398 8.73568 14.0398 9.09766C14.0398 9.45443 14.1245 9.74089 14.2938 9.95703C14.4656 10.1706 14.6948 10.2773 14.9813 10.2773C15.3615 10.2773 15.6909 10.0807 15.9695 9.6875C16.2482 9.29167 16.3875 8.81641 16.3875 8.26172C16.3875 7.90235 16.3016 7.61719 16.1297 7.40625C15.9578 7.19271 15.7286 7.08594 15.4422 7.08594Z"
              fill="white"
            />
            <path
              d="M11.3242 16.0156L11.4258 15.4141H8.95703L8.72266 16.5156H9.63281C9.57552 16.7396 9.45182 16.9258 9.26172 17.0742C9.07162 17.2201 8.85156 17.293 8.60156 17.293C8.27083 17.293 8.01563 17.1875 7.83594 16.9766C7.65886 16.7656 7.57031 16.474 7.57031 16.1016C7.57031 15.7188 7.63151 15.3724 7.75391 15.0625C7.87891 14.7526 8.05208 14.5104 8.27344 14.3359C8.4974 14.1589 8.7487 14.0703 9.02735 14.0703C9.27995 14.0703 9.48828 14.1367 9.65234 14.2695C9.81901 14.4023 9.91016 14.5703 9.92578 14.7734H11.5117C11.5169 14.7318 11.5195 14.6901 11.5195 14.6484C11.5195 14.4036 11.4583 14.168 11.3359 13.9414C11.2161 13.7122 11.0508 13.5117 10.8398 13.3398C10.6315 13.1654 10.3724 13.026 10.0625 12.9219C9.75521 12.8177 9.42578 12.7656 9.07422 12.7656C8.61328 12.7656 8.1836 12.8503 7.78516 13.0195C7.38932 13.1888 7.05208 13.4232 6.77344 13.7227C6.4974 14.0221 6.27995 14.3828 6.1211 14.8047C5.96485 15.2266 5.88672 15.6823 5.88672 16.1719C5.88672 16.9036 6.11719 17.4909 6.57813 17.9336C7.04167 18.3763 7.65104 18.5977 8.40625 18.5977C9.20573 18.5977 9.85677 18.3763 10.3594 17.9336C10.862 17.4909 11.1836 16.8516 11.3242 16.0156Z"
              fill="white"
            />
            <path
              d="M14.0345 18.418C14.3601 18.5378 14.7194 18.5977 15.1127 18.5977C15.5423 18.5977 15.9486 18.5156 16.3314 18.3516C16.7142 18.1849 17.0463 17.9557 17.3275 17.6641C17.6114 17.3698 17.8353 17.0065 17.9994 16.5742C18.166 16.1419 18.2494 15.6719 18.2494 15.1641C18.2494 14.8229 18.1908 14.5065 18.0736 14.2148C17.959 13.9232 17.7936 13.6706 17.5775 13.457C17.3614 13.2409 17.0892 13.0716 16.7611 12.9492C16.433 12.8268 16.0671 12.7656 15.6634 12.7656C15.2338 12.7656 14.8275 12.849 14.4447 13.0156C14.0645 13.1797 13.7338 13.4102 13.4525 13.707C13.1713 14.0013 12.9486 14.3646 12.7845 14.7969C12.6231 15.2266 12.5423 15.694 12.5423 16.1992C12.5423 16.5404 12.6009 16.8581 12.7181 17.1523C12.8379 17.444 13.0059 17.6979 13.222 17.9141C13.4382 18.1276 13.709 18.2956 14.0345 18.418ZM14.9252 14.3555C15.1413 14.1758 15.3744 14.0859 15.6244 14.0859C15.9108 14.0859 16.14 14.1927 16.3119 14.4063C16.4838 14.6172 16.5697 14.9023 16.5697 15.2617C16.5697 15.8164 16.4304 16.2917 16.1517 16.6875C15.8731 17.0807 15.5436 17.2773 15.1634 17.2773C14.877 17.2773 14.6478 17.1706 14.4759 16.957C14.3067 16.7409 14.222 16.4544 14.222 16.0977C14.222 15.7357 14.2858 15.3997 14.4134 15.0898C14.541 14.7799 14.7116 14.5352 14.9252 14.3555Z"
              fill="white"
            />
            <path
              d="M7.2587 1.5H16.7413C17.5463 1.49999 18.2106 1.49998 18.7518 1.54419C19.3139 1.59012 19.8306 1.68868 20.316 1.93598C21.0686 2.31947 21.6805 2.93139 22.064 3.68404C22.3113 4.16937 22.4099 4.68608 22.4558 5.24818C22.5 5.78937 22.5 6.45372 22.5 7.25868V16.7413C22.5 17.5463 22.5 18.2106 22.4558 18.7518C22.4099 19.3139 22.3113 19.8306 22.064 20.316C21.6805 21.0686 21.0686 21.6805 20.316 22.064C19.8306 22.3113 19.3139 22.4099 18.7518 22.4558C18.2106 22.5 17.5463 22.5 16.7413 22.5H7.25868C6.45372 22.5 5.78937 22.5 5.24818 22.4558C4.68608 22.4099 4.16937 22.3113 3.68404 22.064C2.93139 21.6805 2.31947 21.0686 1.93598 20.316C1.68868 19.8306 1.59012 19.3139 1.54419 18.7518C1.49998 18.2106 1.49999 17.5463 1.5 16.7413V7.2587C1.49999 6.45374 1.49998 5.78937 1.54419 5.24818C1.59012 4.68608 1.68868 4.16937 1.93598 3.68404C2.31947 2.93139 2.93139 2.31947 3.68404 1.93598C4.16937 1.68868 4.68608 1.59012 5.24818 1.54419C5.78937 1.49998 6.45374 1.49999 7.2587 1.5ZM5.41104 3.53755C4.97262 3.57337 4.74842 3.6383 4.59202 3.71799C4.2157 3.90974 3.90974 4.2157 3.71799 4.59202C3.6383 4.74842 3.57337 4.97262 3.53755 5.41104C3.50078 5.86113 3.5 6.44342 3.5 7.3V16.7C3.5 17.5566 3.50078 18.1389 3.53755 18.589C3.57337 19.0274 3.6383 19.2516 3.71799 19.408C3.90974 19.7843 4.2157 20.0903 4.59202 20.282C4.74842 20.3617 4.97262 20.4266 5.41104 20.4624C5.86113 20.4992 6.44342 20.5 7.3 20.5H16.7C17.5566 20.5 18.1389 20.4992 18.589 20.4624C19.0274 20.4266 19.2516 20.3617 19.408 20.282C19.7843 20.0903 20.0903 19.7843 20.282 19.408C20.3617 19.2516 20.4266 19.0274 20.4624 18.589C20.4992 18.1389 20.5 17.5566 20.5 16.7V7.3C20.5 6.44342 20.4992 5.86113 20.4624 5.41104C20.4266 4.97262 20.3617 4.74842 20.282 4.59202C20.0903 4.2157 19.7843 3.90974 19.408 3.71799C19.2516 3.6383 19.0274 3.57337 18.589 3.53755C18.1389 3.50078 17.5566 3.5 16.7 3.5H7.3C6.44342 3.5 5.86113 3.50078 5.41104 3.53755Z"
              fill="white"
            />
          </svg>
          <div className={styles.motto}>
            “As long as someone agrees, your work is art.”
            <div style={{ marginTop: 4 }}>Andy Warhol</div>
          </div>

          {/* 读商品角标字段 */}
          <div className={styles.day}>
            {data.commodityConfig?.duration}
            <span>天</span>
          </div>
          <div>无限生成次数</div>
        </div>
        {/* 读商品名称字段 */}
        <div className={styles.title}>{data.productName}</div>
        {/* 读商品文案字段 */}
        {data.productDesc ? <div className={styles.productDesc}>{data.productDesc}</div> : null}
        <div className={styles.price} dangerouslySetInnerHTML={{ __html: data.promotionBanner }}>
          {/* <span>￥</span>
          {getDisplayPrice(data).price}
          <span>/{isMonth ? '月' : '年'}</span> */}
        </div>
        <div className={styles.desc}>
          {[
            '透明底Logo+配色图 (.png)',
            'SVG矢量源文件 (.svg)',
            '六大场景应用效果图 (.png.psd)',
            'Logo动画效果源文件 (.mp4.gif.webp)',
          ].map((text, index) => (
            <div className={styles.descItem} key={index}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
              >
                <path
                  d="M6.3418 11.8101C6.27811 12.1968 5.72209 12.1968 5.65839 11.8101C5.2384 9.26027 3.23993 7.2618 0.690123 6.8418C0.303424 6.77811 0.303423 6.22209 0.690122 6.15839C3.23993 5.7384 5.2384 3.73993 5.65839 1.19012C5.72209 0.803424 6.27811 0.803423 6.3418 1.19012C6.7618 3.73993 8.76027 5.7384 11.3101 6.15839C11.6968 6.22209 11.6968 6.77811 11.3101 6.8418C8.76027 7.2618 6.7618 9.26027 6.3418 11.8101Z"
                  fill="#8CE5FF"
                />
              </svg>

              {text}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.module}>
      {products.length
        ? products?.map((v, index) => {
            return renderCard(v, index);
          })
        : Array.from({ length: 2 }, () => ({})).map((v, index) => {
            return (
              <div
                key={`skeleton_${index}`}
                className={classNames(styles.card, styles.skeleton)}
              ></div>
            );
          })}
    </div>
  );
};

export default React.memo(ProductListUnlimited);
